import { useMemo } from 'react';

import { OrgUsage } from './superAdmin';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import {
  billingCancelEndpoint,
  billingEntitlementEndpoint,
  billingInvoiceEndpoint,
  billingOverviewEndpoint,
  billingPackQuoteEndpoint,
  billingPortalEndpoint,
  billingPurchaseEndpoint,
  billingSubscribeEndpoint,
  billingTopUpEndpoint,
} from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { httpMethod, simplifyBuilder } from 'utils/sra';

export type BillingPlan = {
  id: string;
  code: string;
  name: string;
  description: string;
  monthlyPrice: number;
  includedChecks: number;
  includedChecksPerBank: boolean;
  extraCheckPrice: number | null;
  maxBankAccounts: number;
  showBadge: boolean;
  hasCheckDesigner: boolean;
  hasCustomEmailTemplates: boolean;
  hasApiAccess: boolean;
  hasPrioritySupport: boolean;
  isCustom: boolean;
  stripePriceId: string | null;
  isActive: boolean;
  sortOrder: number;
  isSelfServable: boolean;
};

export type CheckPackTier = {
  id: string;
  minQuantity: number;
  maxQuantity: number | null;
  pricePerCheck: number;
  isActive: boolean;
};

export type CheckPackQuote = {
  quantity: number;
  pricePerCheck: number;
  totalAmount: number;
  minQuantity: number;
  maxQuantity: number;
  requiresSales: boolean;
  unavailableReason: string | null;
  isPurchasable: boolean;
};

export enum SubscriptionStatus {
  Incomplete = 0,
  Active = 1,
  PastDue = 2,
  Canceled = 3,
}

export enum CheckPurchaseStatus {
  Pending = 0,
  Paid = 1,
  Failed = 2,
}

export type OrganizationSubscription = {
  id: string;
  organizationId: string;
  plan: BillingPlan | null;
  status: SubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasStripeSubscription: boolean;
  createdOn: string;
};

export type CheckPurchase = {
  id: string;
  quantity: number;
  pricePerCheck: number;
  totalAmount: number;
  status: CheckPurchaseStatus;
  paidOn: string | null;
  createdOn: string;
};

export type BillingInvoice = {
  id: string;
  number: string | null;
  description: string;
  amountPaid: number;
  status: string;
  isPaid: boolean;
  createdOn: string;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
};

/// What the organization's plan allows, used to hide features it does not include.
export type Entitlement = {
  maxBankAccounts: number;
  showBadge: boolean;
  hasCheckDesigner: boolean;
  hasCustomEmailTemplates: boolean;
  hasApiAccess: boolean;
  hasPrioritySupport: boolean;
  limitsBankAccounts: boolean;
};

export type BillingOverview = {
  usage: OrgUsage;
  subscription: OrganizationSubscription | null;
  currentPlan: BillingPlan | null;
  plans: BillingPlan[];
  packTiers: CheckPackTier[];
  packChecks: number;
  packChecksUsed: number;
  bankAccountCount: number;
  isStripeConfigured: boolean;
};

export type CheckoutSession = {
  url: string | null;
};

export type BillingState = {
  overview: BillingOverview | null;
  entitlement: Entitlement | null;
  quote: CheckPackQuote | null;
  purchases: CheckPurchase[];
  invoices: BillingInvoice[];
  loadBillingOverviewPending: boolean;
  loadPackQuotePending: boolean;
  loadPurchasesPending: boolean;
  loadInvoicesPending: boolean;
  createTopUpCheckoutPending: boolean;
  subscribeToPlanPending: boolean;
  openBillingPortalPending: boolean;
  cancelSubscriptionPending: boolean;
};

export const billingState: BillingState = {
  overview: null,
  entitlement: null,
  quote: null,
  purchases: [],
  invoices: [],
  loadBillingOverviewPending: false,
  loadPackQuotePending: false,
  loadPurchasesPending: false,
  loadInvoicesPending: false,
  createTopUpCheckoutPending: false,
  subscribeToPlanPending: false,
  openBillingPortalPending: false,
  cancelSubscriptionPending: false,
};

const builder = simplifyBuilder(billingState, {});

const loadBillingOverview = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadBillingOverview',
    url: billingOverviewEndpoint(organizationId),
    method: httpMethod.get,
    onSuccess: (_state: BillingState, payload: BillingOverview) => ({
      overview: payload,
    }),
  }),
);

export const loadEntitlement = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadEntitlement',
    url: billingEntitlementEndpoint(organizationId),
    method: httpMethod.get,
    onSuccess: (_state: BillingState, payload: Entitlement) => ({
      entitlement: payload,
    }),
  }),
);

const loadPackQuote = builder.createServerAction(
  (organizationId: string, quantity: number) => ({
    name: 'loadPackQuote',
    url: billingPackQuoteEndpoint(organizationId, quantity),
    method: httpMethod.get,
    onSuccess: (_state: BillingState, payload: CheckPackQuote) => ({
      quote: payload,
    }),
  }),
);

export const clearPackQuote = builder.createReduxAction(() => ({
  name: 'clearPackQuote',
  updater: () => ({ quote: null }),
}));

export const clearBilling = builder.createReduxAction(() => ({
  name: 'clearBilling',
  updater: () => ({
    overview: null,
    quote: null,
    purchases: [],
    invoices: [],
  }),
}));

export const clearEntitlement = builder.createReduxAction(() => ({
  name: 'clearEntitlement',
  updater: () => ({ entitlement: null }),
}));

const loadPurchases = builder.createServerAction((organizationId: string) => ({
  name: 'loadPurchases',
  url: billingPurchaseEndpoint(organizationId),
  method: httpMethod.get,
  onSuccess: (_state: BillingState, payload: CheckPurchase[]) => ({
    purchases: payload,
  }),
}));

const loadInvoices = builder.createServerAction((organizationId: string) => ({
  name: 'loadInvoices',
  url: billingInvoiceEndpoint(organizationId),
  method: httpMethod.get,
  onSuccess: (_state: BillingState, payload: BillingInvoice[]) => ({
    invoices: payload,
  }),
}));

const createTopUpCheckout = builder.createServerAction(
  (organizationId: string, quantity: number) => ({
    name: 'createTopUpCheckout',
    url: billingTopUpEndpoint(organizationId),
    method: httpMethod.post,
    body: { quantity },
    onSuccess: () => ({}),
  }),
);

const subscribeToPlan = builder.createServerAction(
  (organizationId: string, planCode: string) => ({
    name: 'subscribeToPlan',
    url: billingSubscribeEndpoint(organizationId),
    method: httpMethod.post,
    body: { planCode },
    onSuccess: () => ({}),
  }),
);

const openBillingPortal = builder.createServerAction(
  (organizationId: string) => ({
    name: 'openBillingPortal',
    url: billingPortalEndpoint(organizationId),
    method: httpMethod.post,
    onSuccess: () => ({}),
  }),
);

const cancelSubscription = builder.createServerAction(
  (organizationId: string) => ({
    name: 'cancelSubscription',
    url: billingCancelEndpoint(organizationId),
    method: httpMethod.post,
    onSuccess: () => ({}),
  }),
);

export const useBilling = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(appSelectors.organizationId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          loadBillingOverview: loadBillingOverview.bind(null, organizationId),
          loadEntitlement: loadEntitlement.bind(null, organizationId),
          loadPackQuote: loadPackQuote.bind(null, organizationId),
          loadPurchases: loadPurchases.bind(null, organizationId),
          loadInvoices: loadInvoices.bind(null, organizationId),
          createTopUpCheckout: createTopUpCheckout.bind(null, organizationId),
          subscribeToPlan: subscribeToPlan.bind(null, organizationId),
          openBillingPortal: openBillingPortal.bind(null, organizationId),
          cancelSubscription: cancelSubscription.bind(null, organizationId),
          clearPackQuote,
          clearBilling,
          clearEntitlement,
        },
        dispatch,
      ),
    [dispatch, organizationId],
  );
};

export const billingReducer = builder.getReducers();
